/* eslint-disable @typescript-eslint/no-explicit-any */
import { ApiResponse, axiosInstance } from '@/lib/axios';
import { queryOptions, useInfiniteQuery, UseInfiniteQueryOptions, useQuery } from '@tanstack/react-query';
import { QueryConfig } from '@/lib/react-query';
import { TCase } from './types/case.type';

/**
 *
 */
export type TGetMessagesResponse = ApiResponse<TCase[]>;

export interface IGetMessagesRequestParams {
  caseId?: string;
  messageOrder?: string;
  limit?: number | string;
}

export const getMessages = async (params: IGetMessagesRequestParams): Promise<TGetMessagesResponse> => {
  const url = `chat/get-messages`;
  return axiosInstance.get(url, { params });
};

/**
 * Use Get Detail Case React-Query Hook
 */
export const GET_MESSAGES_QUERY_KEY = 'messages';

export const getMessagesQueryOptions = (params: IGetMessagesRequestParams) => {
  return queryOptions({
    queryKey: [GET_MESSAGES_QUERY_KEY, params?.caseId, params?.messageOrder, params?.limit],
    queryFn: () => getMessages(params),
    select: (data) => {
      const reversed = [...(data.payload || [])].reverse();
      return {
        ...data,
        payload: reversed,
      };
    },
  });
};

type UseGetMessagesOptions = {
  params: IGetMessagesRequestParams;
  queryConfig?: QueryConfig<typeof getMessagesQueryOptions>;
};

export const useGetMessages = ({ params, queryConfig }: UseGetMessagesOptions) => {
  return useQuery({
    ...getMessagesQueryOptions(params),
    ...queryConfig,
  });
};

/**
 * Use Get Infinite Detail Case React-Query Hook
 */
export const GET_INFINITE_MESSAGES_QUERY_KEY = 'infinite-messages';
export const DEFAULT_INFINITE_MESSAGES_LIMIT_PARAMS = 50;

type UseGetInfiniteMessagesOptions = {
  params: IGetMessagesRequestParams;
  queryConfig?: UseInfiniteQueryOptions<TGetMessagesResponse, Error>;
};

export const useGetInfiniteMessages = ({ params, queryConfig }: UseGetInfiniteMessagesOptions) => {
  return useInfiniteQuery({
    queryKey: [GET_INFINITE_MESSAGES_QUERY_KEY, params?.caseId, params?.limit],
    queryFn: ({ pageParam = undefined }): Promise<TGetMessagesResponse> =>
      getMessages({
        caseId: params?.caseId,
        messageOrder: pageParam ? String(pageParam) : undefined,
        limit: params?.limit,
      }),
    getNextPageParam: (lastPage) => {
      const messages = lastPage?.payload;
      if (messages?.length > 0) {
        // return messages[0]?.messageOrder; // fetch older messages
        return messages[messages.length - 1]?.messageOrder; // fetch older messages using the last message's order
      }
      return undefined;
    },
    enabled: !!params?.caseId,
    initialPageParam: '' as any,
    ...queryConfig,
  });
};
